import { useEffect, useCallback, useMemo } from 'react';
import { useDatesLocationV3Slots } from '../../../hooks/useDatesLocationV3Slots';
import { useLocationTimeV3Slots } from '../../../hooks/useLocationTimeV3Slots';
import { findFirstNonEmptyV3Slot } from '../../../../../mui/components/sections/DS_FindInMap/utils/findFirstV3Slot';
import { findSecondNonEmptyV3Slot } from '../../../../../mui/components/sections/DS_FindInMap/utils/findSecondV3Slot';
import { useDispatch } from '../../../hooks';
import { setLocationAndTime } from '../../../actions';
import { addMeToWaitlist } from '../waitlist/addMetoWaitlList';
import { pushGTMAppointmentTimeSelectedEvent } from '../../../../../utils/gtm';
import { getCurrentDate, getDaysBetween } from '../../../../../utils/date';

import useLocationStep from './useLocationStep';

export const useLocationTimeSlotV3Selection = (
  locationServicePairs,
  handleSlotsFetched,
  navigation,
  shouldPersonalDataCollected,
  waitlistSuccessLinkLT,
  desiredDays,
  desiredSlots,
  displayedItemCodes,
  resultsForGTM,
  selectedDates,
  showDailyFilters,
  notFetch,
) => {
  const dispatch = useDispatch();
  const updateState = (time, location) => dispatch(setLocationAndTime(location, time));
  const { selectLocation } = useLocationStep();

  // fetch slots only for new locations
  const {
    slotsPerLocation: originalSlots,
    locationLoadingStatus,
    error,
  } = useLocationTimeV3Slots(locationServicePairs, desiredDays, desiredSlots, displayedItemCodes, notFetch);

  const { dateSlotsPerLocation } =
    useDatesLocationV3Slots(locationServicePairs, selectedDates, displayedItemCodes, showDailyFilters) || {};
  // after a successful fetch, call updateFetchedLocations to update the parent component
  useEffect(() => {
    if (originalSlots && !error) {
      const newFetchedLocationCodes = Object.keys(originalSlots);
      handleSlotsFetched(newFetchedLocationCodes);
    }
  }, [originalSlots, error, handleSlotsFetched]);

  const attachSelectionInfo = useCallback(
    locations => {
      const updatedLocations = { ...locations };

      for (const locationCode in updatedLocations) {
        const { error: slotError, slots } = updatedLocations[locationCode];

        if (!slotError && slots) {
          const firstNonEmptySlot = findFirstNonEmptyV3Slot(slots);
          const secondNonEmptySlot = findSecondNonEmptyV3Slot(slots);

          if (firstNonEmptySlot) {
            const { date, time } = firstNonEmptySlot;
            updatedLocations[locationCode].firstSlot = firstNonEmptySlot;
            updatedLocations[locationCode].secondSlot = secondNonEmptySlot;
            updatedLocations[locationCode].firstAvailableDay = date;
            updatedLocations[locationCode].firstAvailableTime = time[0];
            updatedLocations[locationCode].fourAvailableTime = time.slice(0, desiredSlots - 1);
            updatedLocations[locationCode].handleTimeSelect = (timestamp, location) => {
              updateState(timestamp, location);
              selectLocation(location);
            };
            updatedLocations[locationCode].filteredDates = dateSlotsPerLocation?.[locationCode];
            updatedLocations[locationCode].handleGTMAppointmentTimeSelectedEvent = (timestamp, location) => {
              const [sdate, stime] = timestamp.split('T');
              const currentDate = getCurrentDate();
              const daysBetween = getDaysBetween(currentDate, sdate);

              const eventBody = {
                location: location.code,
                hourOfDay: stime,
                selectedDate: sdate,
                currentDate: currentDate,
                daysBetween: daysBetween,
                type: resultsForGTM?.type,
                service: resultsForGTM?.serviceCode,
                funnelType: resultsForGTM?.funnelType,
              };

              pushGTMAppointmentTimeSelectedEvent(eventBody);
            };
          } else {
            // handle case where there are no slots or there is an error
            updatedLocations[locationCode].firstAvailableDay = null;
            updatedLocations[locationCode].firstAvailableTime = null;
            updatedLocations[locationCode].handleTimeSelect = (timestamp, location) => {
              updateState(timestamp, location);
              selectLocation(location);
            };
            updatedLocations[locationCode].handleAddMeToWaitList = location => {
              addMeToWaitlist(
                shouldPersonalDataCollected,
                navigation.goToStepType,
                location.code,
                waitlistSuccessLinkLT,
              );
            };
          }
        }
      }
      return updatedLocations;
    },
    [updateState, selectLocation, locationServicePairs],
  );

  const slotsWithSelectionInfo = useMemo(() => {
    if (error || !originalSlots) return {};
    return attachSelectionInfo(originalSlots);
  }, [originalSlots, error, attachSelectionInfo]);

  return { slots: slotsWithSelectionInfo, error, locationLoadingStatus };
};
